// Provider-priced add-ons on an organisation's page (F-BOOT-17, spec/14 §4.5), as TanStack Query hooks over the
// generated client — `packages/contracts/src/routes/hq-addons.ts`:
//
//   GET   /v1/hq/addons                                          the catalogue: each add-on's price per PC a month
//   POST  /v1/hq/addons    PATCH /v1/hq/addons/{addonKey}        offer an add-on with a price; reprice, rename or stop offering it
//   GET   /v1/hq/organizations/{o}/addons                        the organisation's add-ons and the monthly total
//   POST  /v1/hq/organizations/{o}/addons                        attach one for N PCs, at the catalogue's price
//   PATCH /v1/hq/organizations/{o}/addons/{organizationAddonId}  change the PC count, or cancel
//
// All need `hq:subscriptions` and a second factor; the server checks both on every request.

import type { Addon, AttachOrganizationAddon, CreateAddon, OrganizationAddon, UpdateAddon, UpdateOrganizationAddon } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const addonKeys = {
  catalogue: ['hq', 'addons'] as const,
  organization: (organizationId: string) => ['hq', 'organizations', 'addons', organizationId] as const,
};

/** The add-on catalogue, with each add-on's current price. */
export function useAddonCatalogue(enabled = true) {
  const api = useApi();
  return useQuery({ queryKey: addonKeys.catalogue, queryFn: () => api.listHqAddons(), enabled });
}

/** One organisation's add-ons, active first, and what the active ones come to each month. */
export function useOrganizationAddons(organizationId: string) {
  const api = useApi();
  return useQuery({
    queryKey: addonKeys.organization(organizationId),
    queryFn: () => api.listHqOrganizationAddons({ path: { organizationId } }),
  });
}

/** Attach an add-on for N PCs, at the catalogue's price now. */
export function useAttachAddon(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<OrganizationAddon, unknown, AttachOrganizationAddon>({
    mutationFn: (body) => api.attachHqOrganizationAddon({ path: { organizationId }, body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: addonKeys.organization(organizationId) }),
  });
}

/** Change an add-on's PC count, or cancel it. */
export function useChangeAddon(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<OrganizationAddon, unknown, { readonly organizationAddonId: string; readonly body: UpdateOrganizationAddon }>({
    mutationFn: ({ organizationAddonId, body }) => api.updateHqOrganizationAddon({ path: { organizationId, organizationAddonId }, body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: addonKeys.organization(organizationId) }),
  });
}

/** Offer an add-on in the catalogue, with its price per PC a month (`POST /v1/hq/addons`). */
export function useCreateCatalogueAddon() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Addon, unknown, CreateAddon>({
    mutationFn: (body) => api.createHqAddon({ body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: addonKeys.catalogue }),
  });
}

/** Rename, reprice or stop offering an add-on (`PATCH /v1/hq/addons/{addonKey}`). A new price applies to add-ons attached after it. */
export function useUpdateCatalogueAddon() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Addon, unknown, { readonly addonKey: Addon['key']; readonly body: UpdateAddon }>({
    mutationFn: ({ addonKey, body }) => api.updateHqAddon({ path: { addonKey }, body }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: addonKeys.catalogue }),
  });
}
